import { createTheme, alpha } from '@mui/material/styles';

const gold = '#D1BC8A';
const goldLight = '#E8DEC3';
const goldDark = '#B8A06E';
const navy = '#0F1D2E';
const navyLight = '#1B2D42';
const navyDark = '#0A1119';
const white = '#FFFFFF';
const lightGray = '#FAFAFA';
const darkGray = '#1A1A1A';

const theme = createTheme({
  palette: {
    primary: {
      main: gold,
      light: goldLight,
      dark: goldDark,
      contrastText: navy,
    },
    secondary: {
      main: navy,
      light: navyLight,
      dark: navyDark,
      contrastText: white,
    },
    background: {
      default: '#FFFFFF',
      paper: white,
      dark: navy,
      light: lightGray,
    },
    text: {
      primary: darkGray,
      secondary: alpha(darkGray, 0.65),
      light: white,
      disabled: alpha(darkGray, 0.4),
    },
    divider: alpha(darkGray, 0.08),
    action: {
      hover: alpha(gold, 0.06),
      selected: alpha(gold, 0.12),
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    h1: {
      fontSize: 'clamp(2.75rem, 6vw, 5rem)',
      fontWeight: 800,
      lineHeight: 1.05,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(0, 0, 0, 0.04)',
    '0px 4px 16px rgba(0, 0, 0, 0.06)',
    '0px 6px 24px rgba(0, 0, 0, 0.08)',
    '0px 8px 32px rgba(0, 0, 0, 0.1)',
    '0px 12px 40px rgba(0, 0, 0, 0.12)',
    '0px 16px 48px rgba(0, 0, 0, 0.14)',
    '0px 20px 56px rgba(0, 0, 0, 0.16)',
    '0px 24px 64px rgba(0, 0, 0, 0.18)',
    '0px 28px 72px rgba(0, 0, 0, 0.2)',
    '0px 32px 80px rgba(0, 0, 0, 0.22)',
    '0px 36px 88px rgba(0, 0, 0, 0.24)',
    '0px 40px 96px rgba(0, 0, 0, 0.26)',
    '0px 44px 104px rgba(0, 0, 0, 0.28)',
    '0px 48px 112px rgba(0, 0, 0, 0.3)',
    '0px 52px 120px rgba(0, 0, 0, 0.32)',
    '0px 56px 128px rgba(0, 0, 0, 0.34)',
    '0px 60px 136px rgba(0, 0, 0, 0.36)',
    '0px 64px 144px rgba(0, 0, 0, 0.38)',
    '0px 68px 152px rgba(0, 0, 0, 0.4)',
    '0px 72px 160px rgba(0, 0, 0, 0.42)',
    '0px 76px 168px rgba(0, 0, 0, 0.44)',
    '0px 80px 176px rgba(0, 0, 0, 0.46)',
    '0px 84px 184px rgba(0, 0, 0, 0.48)',
    '0px 88px 192px rgba(0, 0, 0, 0.5)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '14px 32px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        sizeLarge: {
          padding: '16px 40px',
          fontSize: '1.05rem',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
          color: navy,
          boxShadow: `0 4px 20px ${alpha(gold, 0.3)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${goldLight} 0%, ${gold} 100%)`,
            boxShadow: `0 8px 30px ${alpha(gold, 0.4)}`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${navy} 0%, ${navyLight} 100%)`,
          color: white,
          boxShadow: `0 4px 20px ${alpha(navy, 0.3)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${navyLight} 0%, ${navy} 100%)`,
            boxShadow: `0 8px 30px ${alpha(navy, 0.4)}`,
          },
        },
        outlinedPrimary: {
          borderColor: gold,
          color: gold,
          borderWidth: 2,
          backdropFilter: 'blur(10px)',
          '&:hover': {
            borderWidth: 2,
            borderColor: goldLight,
            backgroundColor: alpha(gold, 0.1),
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          border: `1px solid ${alpha(darkGray, 0.06)}`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.01)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
            borderColor: alpha(gold, 0.2),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

export default theme;
export { gold, navy, navyDark, white, lightGray };
